import { GetStaticPropsContext } from "next";
import Head from "next/head";
import { Extension } from "../../../types";
import { getExtensions } from "../../../utils/api";
import Link from "next/link";
import { SectionTitle } from "../../../components/SectionTitle";
import { ImageFallback } from "../../../components/ImageFallback";
import { ArrowLeftIcon } from "../../../components/icons/ArrowLeftIcon";
import Image from "next/image";
import shareIcon from "../../../assets/shareIcon.svg";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { Button } from "../../../components/Button";
import { filterExtensionsByCategory } from "../../../utils/filterExtensions";
import { removeAccents } from "../../../utils/utils";
import { useTranslation } from "react-i18next";
import { ShareModal } from "../../../components/ShareModal";
import { currentLang } from "../../../utils/utils";
import { getLocalePaths } from "../../../utils/i18n.utils";

interface ExtensionPageProps {
  currentExtension: Extension;
  recomendations: Extension[];
  hasImage: boolean;
}

export const getStaticPaths = async () => {
  const extensions = await getExtensions();
  const localePaths = getLocalePaths();

  const paths = localePaths
    .map((locale) => {
      return extensions.apps.map((extension) => {
        return {
          params: {
            extension: extension.subdomain,
            locale: locale.params.locale.toString(),
          },
        };
      });
    })
    .flat();

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const currentExtensionSubdomain = context?.params?.extension as string;
  // const data = await getExtensions()
  const data = await getExtensions();
  const currentExtension = data?.apps?.find(
    (extension) =>
      extension.subdomain.toLowerCase() ===
      currentExtensionSubdomain.toLowerCase(),
  );

  if (!currentExtension) {
    return { notFound: true };
  }

  const extensionCategory = removeAccents(
    currentExtension.name_group.toLowerCase(),
  );

  const extensionsByCatetgory = filterExtensionsByCategory(data.apps);

  const filteredRecomendations = extensionsByCatetgory[
    extensionCategory as keyof typeof extensionsByCatetgory
  ].filter((extension) => extension.name !== currentExtension.name);

  const hasImage = Boolean(
    currentExtension.image[context.params?.locale?.toString() || currentLang],
  );

  return {
    props: {
      hasImage,
      currentExtension,
      recomendations: filteredRecomendations,
    },
    revalidate: 60 * 15,
  };
};

const ExtensionPage = ({
  currentExtension,
  recomendations,
  hasImage,
}: ExtensionPageProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const descriptionRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    console.log(router.query.locale.toString());
    console.log(currentExtension.description);
    console.log(router.query.locale.toString() in currentExtension.description);
    const $p = descriptionRef.current;

    const domParser = new DOMParser();

    if (router.query.locale.toString() in currentExtension.description) {
      const string = domParser.parseFromString(
        currentExtension.description[router.query.locale.toString()],
        "text/html",
      );
      $p.innerHTML = string.querySelector("body").innerHTML;
    } else {
      console.log(currentLang);
      const string = domParser.parseFromString(
        currentExtension.description[currentLang],
        "text/html",
      );
      $p.innerHTML = string.querySelector("body").innerHTML;
    }

    return () => {
      $p.innerHTML = "";
    };
  }, [router.query]);

  return (
    <>
      <Head>
        <title>{currentExtension.name}</title>
        <meta
          name="description"
          key="desc"
          content={`${currentExtension.name}`}
        />
        <meta property="og:locale" content={currentLang} />
        <meta
          property="og:url"
          content={`https://auraview.auravant.com/marketplace#install=${currentExtension.client_id}`}
        />
        <meta property="og:type" content="product" />
        <meta
          property="og:logo"
          content={`https://media.licdn.com/dms/image/C4E0BAQGWvgmUoW_xHw/company-logo_200_200/0/1630590067515/auravant_logo?e=2147483647&v=beta&t=3-Pi0B0f5oKmfsx_E72xWLPeY7FnA9-gXsS_pcYzRWY`}
        />
        <meta
          property="og:title"
          content={`Auravant + ${currentExtension.name}`}
        />
        <meta
          property="og:description"
          content={`${currentExtension.description[currentLang]}`}
        />
        <meta
          property="og:image"
          content={
            currentExtension.image[
              router.query.locale.toString() || currentLang
            ]
          }
        />
      </Head>
      <section className="p-4 ">
        <SectionTitle>
          <Link
            href={`/${router.query.locale?.toString()}`}
            className="flex gap-2 font-light"
          >
            <ArrowLeftIcon />
            <span className="capitalize">{t("goBack.volver")}</span>
          </Link>
        </SectionTitle>
        <div className={`flex flex-col justify-center gap-4 pt-4 md:flex-row`}>
          <div className={`hidden lg:block ${!hasImage ? "hidden" : "block"} `}>
            {hasImage && (
              <ImageFallback
                src={
                  currentExtension.image[
                    router.query.locale.toString() || currentLang
                  ]
                }
                height={!hasImage ? 0 : 400}
                width={!hasImage ? 0 : 480}
                alt="extension-imags"
                priority
              />
            )}
          </div>
          <div>
            <h1 className="flex items-center pb-2 text-2xl font-semibold text-aura-blue lg:pb-0">
              <span>{currentExtension.name}</span>
              <button
                onClick={() => setIsOpen(true)}
                className="pl-2 pt-1 text-xs font-light"
              >
                <Image
                  src={shareIcon}
                  width={16}
                  height={16}
                  alt="share-icon"
                  priority
                />
              </button>
              <ShareModal
                href={currentExtension.client_id}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={currentExtension.name}
              />
            </h1>
            <div className="block lg:hidden">
              {hasImage && (
                <ImageFallback
                  src={
                    currentExtension.image[
                      router.query.locale.toString() || currentLang
                    ]
                  }
                  height={!hasImage ? 0 : 720}
                  width={!hasImage ? 0 : 760}
                  alt="extension-imags"
                  priority
                />
              )}
            </div>
            <p className="max-w-[60ch] py-4" ref={descriptionRef}></p>

            <Link
              passHref
              href={`https://auraview.auravant.com/marketplace#install=${currentExtension.client_id}?utm_source=mktplace`}
              target="_blank"
            >
              <Button
                variant="primary"
                className={`flex h-12 w-full items-center justify-center ${
                  currentExtension.purchase_details.is_purchasable
                    ? "bg-premium-yellow"
                    : ""
                }`}
              >
                {currentExtension.purchase_details.is_purchasable
                  ? t("btn.premium").toUpperCase()
                  : t("btn.standard").toUpperCase()}
              </Button>
            </Link>

            {recomendations.length !== 0 && (
              <div className="pt-4">
                <h2 className="pb-2">{t("extension.recomendations")}</h2>
                <div className="scrollbar max-w-[60ch] overflow-scroll">
                  <div className="flex gap-8  pb-4">
                    {recomendations.map((recomendation) => {
                      return (
                        <Link
                          href={`/${router.query.locale?.toString()}/extension/${
                            recomendation.subdomain
                          }`}
                          key={recomendation.client_id}
                        >
                          <div className="flex h-16 w-12 flex-col justify-center">
                            <Image
                              className="shadow-around"
                              src={recomendation.logo_url}
                              height={50}
                              width={50}
                              alt={`logo ${recomendation.name}`}
                            />
                            <h3 className="truncate text-xs">
                              {recomendation.name}
                            </h3>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default ExtensionPage;
